import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Circle, ArrowLeft, Award, Upload } from 'lucide-react';
import { toast } from 'sonner';
import SubmissionLesson from '@/components/SubmissionLesson';

export default function CoursePlayer() {
  const { course_id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [active, setActive] = useState({ moduleIdx: 0, lessonIdx: 0, mode: 'lesson' });
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/courses/${course_id}`);
    setCourse(data.course); setEnrollment(data.enrollment);
  };
  useEffect(() => { load(); }, [course_id]);

  if (!course) return <div className="min-h-screen flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-navy/20 border-t-navy animate-spin" /></div>;

  const progress = enrollment?.progress || {};
  const totalItems = course.modules.reduce((acc, m) => acc + m.lessons.length + (m.quiz ? 1 : 0), 0);
  const doneItems = course.modules.reduce((acc, m) => {
    let c = m.lessons.filter(l => progress[l.lesson_id]?.completed).length;
    if (m.quiz && progress[m.quiz.quiz_id]?.passed) c += 1;
    return acc + c;
  }, 0);
  const pct = totalItems ? Math.round((doneItems / totalItems) * 100) : 0;

  const currentModule = course.modules[active.moduleIdx];
  const currentLesson = active.mode === 'lesson' ? currentModule.lessons[active.lessonIdx] : null;

  const markLessonComplete = async (lesson) => {
    if (progress[lesson.lesson_id]?.completed) return;
    await api.post('/lms/lesson/complete', { course_id, lesson_id: lesson.lesson_id });
    await load();
    toast.success('Lesson completed');
  };

  const submitQuiz = async () => {
    const res = await api.post('/lms/quiz/submit', { course_id, quiz_id: currentModule.quiz.quiz_id, answers: quizAnswers });
    setQuizResult(res.data);
    await load();
    if (res.data.passed) {
      toast.success(`Quiz passed (${Math.round(res.data.score * 100)}%)`);
      if (res.data.course_complete && res.data.certificate_number) {
        toast.success(`Certificate issued: ${res.data.certificate_number}`);
      }
    } else {
      toast.error(`Score ${Math.round(res.data.score * 100)}%. Try again.`);
    }
  };

  const setQuizAnswer = (qi, value) => {
    setQuizAnswers(prev => { const a = [...prev]; a[qi] = value; return a; });
  };

  return (
    <div className="min-h-screen bg-trjlight" data-testid="course-player">
      <div className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <Link to="/dashboard" className="text-white/70 hover:text-gold inline-flex items-center text-sm"><ArrowLeft className="h-4 w-4 mr-1" /> Dashboard</Link>
            <h1 className="font-display font-semibold text-xl lg:text-2xl mt-1">{course.title}</h1>
          </div>
          <div className="w-64 text-right">
            <div className="text-xs uppercase tracking-widest text-gold">Course Progress</div>
            <Progress value={pct} className="h-2 mt-2 bg-white/20" />
            <div className="text-xs mt-1 text-white/70">{pct}% complete</div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 xl:col-span-3 bg-white border border-slate-200 rounded-xl p-3 h-fit lg:sticky lg:top-24 max-h-[80vh] overflow-y-auto thin-scroll">
          {course.modules.map((m, mi) => (
            <div key={m.module_id} className="mb-2">
              <div className="px-3 py-2 text-xs uppercase tracking-widest text-gold font-semibold">{m.title}</div>
              {m.lessons.map((l, li) => {
                const done = progress[l.lesson_id]?.completed;
                const isActive = active.mode === 'lesson' && active.moduleIdx === mi && active.lessonIdx === li;
                return (
                  <button key={l.lesson_id}
                    data-testid={`lesson-item-${l.lesson_id}`}
                    onClick={() => { setActive({ moduleIdx: mi, lessonIdx: li, mode: 'lesson' }); setQuizResult(null); }}
                    className={`w-full text-left px-3 py-2.5 rounded-md flex items-start gap-2 text-sm ${isActive ? 'bg-navy text-white' : 'hover:bg-slate-100 text-slate-700'}`}>
                    {done ? <CheckCircle2 className="h-4 w-4 text-trjgreen mt-0.5 shrink-0" /> : <Circle className="h-4 w-4 mt-0.5 shrink-0 opacity-50" />}
                    <span>{l.title}</span>
                  </button>
                );
              })}
              {m.quiz && (
                <button onClick={() => { setActive({ moduleIdx: mi, lessonIdx: 0, mode: 'quiz' }); setQuizAnswers([]); setQuizResult(null); }}
                  data-testid={`quiz-item-${m.quiz.quiz_id}`}
                  className={`w-full text-left px-3 py-2.5 rounded-md flex items-start gap-2 text-sm font-semibold ${active.mode === 'quiz' && active.moduleIdx === mi ? 'bg-gold/20 text-navy' : 'hover:bg-slate-100 text-navy'}`}>
                  <Award className="h-4 w-4 mt-0.5 text-gold" /> {m.quiz.title}
                  {progress[m.quiz.quiz_id]?.passed && <CheckCircle2 className="h-4 w-4 text-trjgreen ml-auto" />}
                </button>
              )}
            </div>
          ))}
        </aside>

        <main className="lg:col-span-8 xl:col-span-9">
          {active.mode === 'lesson' && currentLesson && currentLesson.content_type === 'submission' && (
            <div className="bg-white border border-slate-200 rounded-xl p-6 lg:p-8">
              <div className="text-xs uppercase tracking-widest text-gold font-semibold">{currentModule.title}</div>
              <SubmissionLesson courseId={course_id} lesson={currentLesson} onReload={load} />
            </div>
          )}
          {active.mode === 'lesson' && currentLesson && currentLesson.content_type !== 'submission' && (
            <div className="bg-white border border-slate-200 rounded-xl p-6 lg:p-8" data-testid={`lesson-content-${currentLesson.lesson_id}`}>
              <div className="text-xs uppercase tracking-widest text-gold font-semibold">{currentModule.title}</div>
              <h2 className="font-display font-semibold text-2xl text-navy mt-2">{currentLesson.title}</h2>

              {currentLesson.content_type === 'video' && currentLesson.video_url && (
                <div className="mt-6 aspect-video bg-black rounded-md overflow-hidden">
                  <iframe src={currentLesson.video_url} title={currentLesson.title} allowFullScreen className="w-full h-full" />
                </div>
              )}

              <div className="mt-6 text-slate-700 leading-relaxed">
                {currentLesson.body || 'Watch the lesson video above. When you\'re ready, mark this lesson complete to advance.'}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Button onClick={() => markLessonComplete(currentLesson)}
                  className={progress[currentLesson.lesson_id]?.completed ? 'btn-navy' : 'btn-gold'}
                  data-testid="lesson-complete-btn">
                  {progress[currentLesson.lesson_id]?.completed ? 'Completed ✓' : 'Mark lesson complete'}
                </Button>
                <Button variant="outline" className="border-navy/20 text-navy hover:bg-navy hover:text-white"
                  onClick={() => {
                    const next = currentModule.lessons[active.lessonIdx + 1];
                    if (next) setActive({ ...active, lessonIdx: active.lessonIdx + 1 });
                    else if (currentModule.quiz) setActive({ ...active, mode: 'quiz' });
                    else if (course.modules[active.moduleIdx + 1]) setActive({ moduleIdx: active.moduleIdx + 1, lessonIdx: 0, mode: 'lesson' });
                  }} data-testid="next-lesson-btn">Next →</Button>
              </div>
            </div>
          )}

          {active.mode === 'quiz' && currentModule.quiz && (
            <div className="bg-white border border-slate-200 rounded-xl p-6 lg:p-8" data-testid={`quiz-${currentModule.quiz.quiz_id}`}>
              <div className="text-xs uppercase tracking-widest text-gold font-semibold">{currentModule.title}</div>
              <h2 className="font-display font-semibold text-2xl text-navy mt-2">{currentModule.quiz.title}</h2>
              <p className="text-sm text-slate-500 mt-1">Pass threshold: {Math.round(currentModule.quiz.pass_threshold * 100)}%</p>

              <div className="mt-6 space-y-6">
                {currentModule.quiz.questions.map((q, qi) => (
                  <div key={q.question_id} className="border border-slate-200 rounded-md p-5">
                    <div className="font-display font-semibold text-navy">{qi + 1}. {q.prompt}</div>
                    <div className="mt-3 space-y-2">
                      {q.options.map((opt, oi) => (
                        <label key={oi} className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition-colors ${quizAnswers[qi] === oi ? 'border-navy bg-navy/5' : 'border-slate-200 hover:border-navy/40'}`}>
                          <input type="radio" name={`q-${qi}`} className="accent-navy" checked={quizAnswers[qi] === oi} onChange={() => setQuizAnswer(qi, oi)} data-testid={`quiz-${qi}-opt-${oi}`} />
                          <span className="text-slate-700">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {quizResult && (
                <div className={`mt-6 p-5 rounded-md ${quizResult.passed ? 'bg-trjgreen/10 text-trjgreen border border-trjgreen/30' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  <div className="font-semibold">Score: {Math.round(quizResult.score * 100)}% ({quizResult.correct}/{quizResult.total})</div>
                  {quizResult.passed
                    ? <p className="text-sm mt-1">{quizResult.course_complete ? `Course complete! Certificate ${quizResult.certificate_number} issued.` : 'Module quiz passed.'}</p>
                    : <p className="text-sm mt-1">Review the lessons and try again.</p>}
                  {quizResult.course_complete && (
                    <Button onClick={() => navigate('/dashboard')} className="btn-navy mt-3" data-testid="back-to-dashboard">Back to Dashboard</Button>
                  )}
                </div>
              )}

              <Button onClick={submitQuiz} disabled={quizAnswers.length < currentModule.quiz.questions.length} className="btn-gold mt-6 h-11 px-6" data-testid="quiz-submit-btn">
                Submit answers
              </Button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
